import React from 'react';
import { Popover, Card, Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';

type TableInputSearchPopoverProps = {
  children: React.ReactNode;
  initialValue: string;
  handleChange: (keyword: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

const TableInputSearchPopover: React.FC<TableInputSearchPopoverProps> = props => {
  const { t } = useTranslation();

  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const formMethods = useForm({
    defaultValues: {
      searchKeyword: props.initialValue,
    },
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, watch, reset, formState } = formMethods;
  const { isSubmitting, dirtyFields } = formState;

  React.useEffect(() => {
    setTimeout(() => {
      const input = document.getElementById('filterSearch');
      if (popoverVisible && input) input.focus();
    }, 100);
  }, [popoverVisible]);

  React.useEffect(() => {
    if (!popoverVisible) {
      reset({ searchKeyword: props.initialValue }, { keepDirty: false });
    }
  }, [popoverVisible, props.initialValue, reset]);

  const handleVisibleChange = (visible: boolean) => {
    setPopoverVisible(visible);
    if (!visible) {
      reset({ searchKeyword: props.initialValue }, { keepDirty: false });
    }
    if (visible && props.onOpen) props.onOpen();
    else if (props.onClose) props.onClose();
  };

  const onSubmit = (values: { searchKeyword: string }) => {
    props.handleChange(values.searchKeyword);
    handleVisibleChange(false);
  };

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => handleVisibleChange(visible)}
      overlayClassName="table-filter-popover"
      placement="bottomLeft"
      destroyTooltipOnHide
      content={
        <Card>
          <FormProvider {...formMethods}>
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
              <div className="table-popover-search">
                <Input.Search
                  id="filterSearch"
                  value={watch('searchKeyword')}
                  onChange={e => setValue('searchKeyword', e.target.value)}
                />
              </div>
              <div className="flex-col items-end mt-3 mr-3">
                <div>
                  <Button
                    onClick={() => {
                      reset({ searchKeyword: '' });
                      handleSubmit(onSubmit)();
                    }}
                    className="drawer-submit__bottom-cancel"
                  >
                    {t('common:reset')}
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={!dirtyFields.searchKeyword || isSubmitting}
                  >
                    {t('common:ok')}
                  </Button>
                </div>
              </div>
            </Form>
          </FormProvider>
        </Card>
      }
      trigger="click"
    >
      {props.children}
    </Popover>
  );
};

export default TableInputSearchPopover;
